import { useEffect, useRef, useState } from "react";
import { useInView } from "react-intersection-observer";

export type AnimationVariant = 
  | 'fadeUp' 
  | 'fadeDown' 
  | 'fadeLeft' 
  | 'fadeRight'
  | 'scale' 
  | 'slideLeft' 
  | 'slideRight'
  | 'blur'
  | 'rotate';

interface AnimationConfig {
  variant?: AnimationVariant;
  threshold?: number;
  delay?: number;
  duration?: number;
  once?: boolean;
}

const getVariantStyles = (variant: AnimationVariant, isVisible: boolean, duration: number) => {
  const baseTransition = `all ${duration}ms cubic-bezier(0.4, 0, 0.2, 1)`;
  
  const variants = {
    fadeUp: {
      initial: { opacity: 0, transform: 'translateY(40px)' },
      animate: { opacity: 1, transform: 'translateY(0)' }
    },
    fadeDown: {
      initial: { opacity: 0, transform: 'translateY(-40px)' },
      animate: { opacity: 1, transform: 'translateY(0)' }
    },
    fadeLeft: {
      initial: { opacity: 0, transform: 'translateX(60px)' },
      animate: { opacity: 1, transform: 'translateX(0)' }
    },
    fadeRight: {
      initial: { opacity: 0, transform: 'translateX(-60px)' },
      animate: { opacity: 1, transform: 'translateX(0)' }
    },
    scale: {
      initial: { opacity: 0, transform: 'scale(0.85)' },
      animate: { opacity: 1, transform: 'scale(1)' }
    },
    slideLeft: {
      initial: { opacity: 0, transform: 'translateX(80px)' },
      animate: { opacity: 1, transform: 'translateX(0)' }
    },
    slideRight: {
      initial: { opacity: 0, transform: 'translateX(-80px)' },
      animate: { opacity: 1, transform: 'translateX(0)' }
    },
    blur: {
      initial: { opacity: 0, filter: 'blur(10px)' },
      animate: { opacity: 1, filter: 'blur(0)' }
    },
    rotate: {
      initial: { opacity: 0, transform: 'rotate(-5deg) scale(0.95)' },
      animate: { opacity: 1, transform: 'rotate(0) scale(1)' }
    }
  };

  const styles = variants[variant];
  return {
    ...((isVisible ? styles.animate : styles.initial) as React.CSSProperties),
    transition: baseTransition
  };
};

export const useAdvancedScrollAnimation = ({
  variant = 'fadeUp',
  threshold = 0.1,
  delay = 0,
  duration = 800,
  once = true
}: AnimationConfig = {}) => {
  const { ref, inView } = useInView({
    threshold,
    triggerOnce: once,
  });
  
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (inView && !isVisible) {
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, delay);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [inView, delay, isVisible]);

  const style = getVariantStyles(variant, isVisible, duration);

  return {
    ref,
    isVisible,
    style,
    className: isVisible ? 'animate-in' : 'animate-out'
  };
};

// Staggered children animation
export const useStaggeredAnimation = (
  itemCount: number,
  baseDelay: number = 0,
  staggerDelay: number = 100
) => {
  const animations = Array.from({ length: itemCount }, (_, i) => 
    useAdvancedScrollAnimation({
      variant: 'fadeUp',
      delay: baseDelay + (i * staggerDelay),
      threshold: 0.05
    })
  );

  return animations;
};
